import React from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Divider,
} from '@mui/material';

const Privacy: React.FC = () => {
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Privacy Policy
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <section>
            <Typography variant="h5" component="h2" gutterBottom>
              1. Introduction
            </Typography>
            <Typography variant="body1" paragraph>
              Welcome to Taboo Game ("we", "our", or "us").
            </Typography>
            <Typography variant="body1" paragraph>
              We respect your privacy and are committed to protecting it. This Privacy Policy explains what information we collect, how we use it, and your rights when using our mobile application.
            </Typography>
          </section>

          <Divider sx={{ my: 2 }} />

          <section>
            <Typography variant="h5" component="h2" gutterBottom>
              2. Information We Collect
            </Typography>
            <Typography variant="body1" paragraph>
              We collect only limited information necessary to operate and improve the App.
            </Typography>
            
            <Typography variant="h6" component="h3" sx={{ mt: 2, mb: 1 }}>
              a) Feedback Information
            </Typography>
            <Typography variant="body1" paragraph>
              If you choose to contact us through the in-app feedback form, we may collect:
            </Typography>
            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
              <li>
                <Typography variant="body1" component="span">
                  Name
                </Typography>
              </li>
              <li>
                <Typography variant="body1" component="span">
                  Email address
                </Typography>
              </li>
            </Box>
            <Typography variant="body1" paragraph>
              This information is voluntarily provided by you and is used only to respond to your feedback or support requests.
              Submitting this information is optional, and the App can be used without providing it.
            </Typography>

            <Typography variant="h6" component="h3" sx={{ mt: 2, mb: 1 }}>
              b) Game Data (Non-Personal)
            </Typography>
            <Typography variant="body1" paragraph>
              We may collect non-personal game-related data such as:
            </Typography>
            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
              <li>
                <Typography variant="body1" component="span">
                  Game progress
                </Typography>
              </li>
              <li>
                <Typography variant="body1" component="span">
                  Scores
                </Typography>
              </li>
              <li>
                <Typography variant="body1" component="span">
                  Deck usage
                </Typography>
              </li>
            </Box>
            <Typography variant="body1" paragraph>
              This data does not identify you personally and is used only to improve gameplay experience.
            </Typography>

            <Typography variant="h6" component="h3" sx={{ mt: 2, mb: 1 }}>
              c) Advertising Data
            </Typography>
            <Typography variant="body1" paragraph>
              Our App uses third-party advertising services, such as Google AdMob.
              These services may collect anonymous, non-personal information (for example, device identifiers) to serve ads in accordance with their own privacy policies.
            </Typography>
            <Typography variant="body1" paragraph>
              We do not receive or store personal data from advertising providers.
            </Typography>
          </section>

          <Divider sx={{ my: 2 }} />

          <section>
            <Typography variant="h5" component="h2" gutterBottom>
              3. How We Use Your Information
            </Typography>
            <Typography variant="body1" paragraph>
              We use collected information only to:
            </Typography>
            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
              <li>
                <Typography variant="body1" component="span">
                  Respond to user feedback and support requests
                </Typography>
              </li>
              <li>
                <Typography variant="body1" component="span">
                  Improve app functionality and gameplay experience
                </Typography>
              </li>
              <li>
                <Typography variant="body1" component="span">
                  Ensure app stability and performance
                </Typography>
              </li>
            </Box>
            <Typography variant="body1" paragraph>
              We do not use your personal data for advertising, marketing, or profiling purposes.
            </Typography>
          </section>

          <Divider sx={{ my: 2 }} />

          <section>
            <Typography variant="h5" component="h2" gutterBottom>
              4. Data Sharing
            </Typography>
            <Typography variant="body1" paragraph>
              We do not sell or rent your personal data.
            </Typography>
            <Typography variant="body1" paragraph>
              We may share limited data only in these cases:
            </Typography>
            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
              <li>
                <Typography variant="body1" component="span">
                  With trusted third-party services (such as advertising SDKs) strictly for app functionality
                </Typography>
              </li>
              <li>
                <Typography variant="body1" component="span">
                  When required by law or legal process
                </Typography>
              </li>
            </Box>
          </section>

          <Divider sx={{ my: 2 }} />

          <section>
            <Typography variant="h5" component="h2" gutterBottom>
              5. Data Security
            </Typography>
            <Typography variant="body1" paragraph>
              We take reasonable technical and organizational measures to protect your information.
              However, no electronic transmission or storage method is completely secure, and we cannot guarantee absolute security.
            </Typography>
          </section>

          <Divider sx={{ my: 2 }} />

          <section>
            <Typography variant="h5" component="h2" gutterBottom>
              6. Data Retention
            </Typography>
            <Typography variant="body1" paragraph>
              Personal data submitted via the feedback form is retained only as long as necessary to respond to your request and is deleted thereafter unless legally required otherwise.
            </Typography>
          </section>

          <Divider sx={{ my: 2 }} />

          <section>
            <Typography variant="h5" component="h2" gutterBottom>
              7. Your Rights
            </Typography>
            <Typography variant="body1" paragraph>
              Depending on your location, you may have the right to:
            </Typography>
            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
              <li>
                <Typography variant="body1" component="span">
                  Access your personal data
                </Typography>
              </li>
              <li>
                <Typography variant="body1" component="span">
                  Request correction or deletion
                </Typography>
              </li>
              <li>
                <Typography variant="body1" component="span">
                  Withdraw consent at any time
                </Typography>
              </li>
            </Box>
            <Typography variant="body1" paragraph>
              To exercise these rights, please contact us using the information below.
            </Typography>
          </section>

          <Divider sx={{ my: 2 }} />

          <section>
            <Typography variant="h5" component="h2" gutterBottom>
              8. Children's Privacy
            </Typography>
            <Typography variant="body1" paragraph>
              Our App is not intended for children under the age of 13.
              We do not knowingly collect personal information from children under 13.
              If you believe a child has provided us with personal data, please contact us so we can remove it.
            </Typography>
          </section>

          <Divider sx={{ my: 2 }} />

          <section>
            <Typography variant="h5" component="h2" gutterBottom>
              9. International Data Transfers
            </Typography>
            <Typography variant="body1" paragraph>
              Your information may be processed outside your country of residence where data protection laws may differ. By using the App, you consent to such processing.
            </Typography>
          </section>

          <Divider sx={{ my: 2 }} />

          <section>
            <Typography variant="h5" component="h2" gutterBottom>
              10. Changes to This Privacy Policy
            </Typography>
            <Typography variant="body1" paragraph>
              We may update this Privacy Policy from time to time.
              Any changes will be posted on this page with an updated revision date.
            </Typography>
          </section>

          <Divider sx={{ my: 2 }} />

          <section>
            <Typography variant="h5" component="h2" gutterBottom>
              11. Contact Us
            </Typography>
            <Typography variant="body1" paragraph>
              If you have any questions about this Privacy Policy or your data, please contact us at:
            </Typography>
            <Box sx={{ pl: 2, mt: 1 }}>
              <Typography variant="body1" paragraph sx={{ mb: 0.5 }}>
                Email: taboorush.info@gmail.com
              </Typography>
            </Box>
          </section>
        </Box>
      </Paper>
    </Container>
  );
};

export default Privacy;
